import { Head, Link, router } from "@inertiajs/react"
import { AdminShell } from "@/components/AdminShell"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { PlusCircle, Pencil, Trash2 } from "lucide-react"

type QuestionRow = {
  id:            number
  content:       string
  answers_count: number
  difficulty:    string | null
  has_more_info: boolean
  updated_at:    string
}

type Props = { questions: QuestionRow[] }

const DIFFICULTY_TONE: Record<string, "signal" | "accent" | "muted"> = {
  easy:   "signal",
  medium: "accent",
  hard:   "muted",
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" })
}

function destroy(id: number) {
  if (!confirm("Delete this question and all its answers? This cannot be undone.")) return
  router.delete(`/admin/questions/${id}`)
}

export default function AdminQuestionsIndex({ questions }: Props) {
  return (
    <>
      <Head title="Questions — Admin">
        <meta name="description" content="Manage trivia questions." />
        <meta property="og:title" content="Questions — Admin" />
        <meta property="og:description" content="Manage trivia questions." />
      </Head>
      <AdminShell>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1>Trivia Questions</h1>
            <p className="text-sm text-ink-muted mt-1">{questions.length} question{questions.length !== 1 ? "s" : ""}</p>
          </div>
          <Link href="/admin/questions/new">
            <Button>
              <PlusCircle className="w-4 h-4 mr-2" />
              Add question
            </Button>
          </Link>
        </div>

        <div className="rounded-xl border border-hairline bg-surface overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-hairline bg-stone-50 dark:bg-stone-900">
                <th className="text-left px-4 py-3 font-medium text-ink-muted">#</th>
                <th className="text-left px-4 py-3 font-medium text-ink-muted">Question</th>
                <th className="text-center px-4 py-3 font-medium text-ink-muted">Answers</th>
                <th className="text-center px-4 py-3 font-medium text-ink-muted">Difficulty</th>
                <th className="text-center px-4 py-3 font-medium text-ink-muted">More Info</th>
                <th className="text-right px-4 py-3 font-medium text-ink-muted">Updated</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {questions.map(q => (
                <tr key={q.id} className="border-b border-hairline last:border-0 hover:bg-stone-50 dark:hover:bg-stone-900/50">
                  <td className="px-4 py-3 text-ink-muted tabular-nums text-xs">{q.id}</td>
                  <td className="px-4 py-3 font-medium text-ink-display max-w-xl">
                    <span className="line-clamp-2">{q.content}</span>
                  </td>
                  <td className="px-4 py-3 text-center text-ink-muted tabular-nums">{q.answers_count}</td>
                  <td className="px-4 py-3 text-center">
                    {q.difficulty ? (
                      <Badge tone={DIFFICULTY_TONE[q.difficulty]}>{q.difficulty}</Badge>
                    ) : (
                      <span className="text-xs text-ink-muted">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {q.has_more_info ? (
                      <Badge tone="signal">Yes</Badge>
                    ) : (
                      <span className="text-xs text-ink-muted">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right text-xs text-ink-muted">{formatDate(q.updated_at)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <Link href={`/admin/questions/${q.id}/edit`} className="p-1.5 rounded hover:bg-stone-100 dark:hover:bg-stone-800 text-ink-muted hover:text-ink-display transition-colors">
                        <Pencil className="w-3.5 h-3.5" />
                      </Link>
                      <button onClick={() => destroy(q.id)} className="p-1.5 rounded hover:bg-red-50 dark:hover:bg-red-950/30 text-ink-muted hover:text-red-600 dark:hover:text-red-400 transition-colors">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </AdminShell>
    </>
  )
}
